import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import api from "../../api/api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function Profile() {
  const [userData, setUserData] = useState({
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    groups: [],
  });

  const [password, setPassword] = useState(""); // ✅ Separate state for password
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false); // ✅ Dialog for success confirmation

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await api.get("/user-profile/");
        setUserData(response.data);
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
  }, []);

  const handleChange = (e) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const updatedData = { ...userData };

      // ✅ Only include password if it's entered
      if (password) {
        updatedData.password = password;
      }

      const response = await api.put("/user-profile/", updatedData);

      setSuccessMessage("Profile updated successfully!");
      setPassword(""); // ✅ Clear password field after update
      setIsEditing(false);
      setDialogOpen(true); // ✅ Open confirmation dialog
    } catch (error) {
      console.error("Profile update error:", error);

      if (error.response && error.response.data.error) {
        setErrorMessage(error.response.data.error.join(" ")); // ✅ Catch password validation errors
      } else {
        setErrorMessage("Error updating profile. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <Card sx={{ padding: 3 }}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          Profile Information
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Username"
              fullWidth
              value={userData.username}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Email"
              fullWidth
              name="email"
              value={userData.email}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="First Name"
              fullWidth
              name="first_name"
              value={userData.first_name}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Last Name"
              fullWidth
              name="last_name"
              value={userData.last_name}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </Grid>
          {isEditing && (
            <Grid item xs={12} md={6}>
              <TextField
                label="New Password"
                type="password"
                fullWidth
                value={password}
                onChange={handlePasswordChange}
                helperText="Leave blank to keep current password."
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              label="Groups"
              fullWidth
              value={userData.groups.join(", ") || "No groups assigned"}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        {/* ✅ Error Message Handling */}
        {errorMessage && (
          <MDBox mt={2} color="error">
            <MDTypography color="error">{errorMessage}</MDTypography>
          </MDBox>
        )}

        <MDBox mt={3} display="flex" justifyContent="space-between">
          {isEditing ? (
            <>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsEditing(true)}
            >
              Edit Profile
            </Button>
          )}
        </MDBox>
      </Card>

      {/* ✅ Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Profile Updated</DialogTitle>
        <DialogContent>
          <DialogContentText>{successMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Profile;
